import React from 'react';
import {
  computeCustomRemainder,
  computeEqualSplitAmount,
  computeEqualSplitAmounts,
  isCustomSplitValid,
} from './splitPayment.utils';

export type SplitDialogMode = 'root' | 'equal' | 'custom';

export type UseSplitPaymentDialogStateArgs = {
  isOpen: boolean;
  totalAmount: number;
  /** with-tip grand total split across all tabs (equal + custom + summary); defaults to totalAmount */
  equalSplitTotal?: number;
};

export type SplitPaymentDialogState = {
  mode: SplitDialogMode;
  equalSplitCount: number | null;
  customSplitInput: string;
  equalSplitAmount: number;
  equalSplitAmounts: number[];
  customSplitAmount: number;
  customSplitRemainder: number;
  customValid: boolean;
  setMode: (mode: SplitDialogMode) => void;
  setEqualSplitCount: (count: number | null) => void;
  handleCustomSplitInputChange: (value: string) => void;
  resetToRoot: () => void;
};

export function useSplitPaymentDialogState(
  args: UseSplitPaymentDialogStateArgs,
): SplitPaymentDialogState {
  const { isOpen, totalAmount, equalSplitTotal = totalAmount } = args;
  const [mode, setMode] = React.useState<SplitDialogMode>('root');
  const [equalSplitCount, setEqualSplitCount] = React.useState<number | null>(null);
  const [customSplitInput, setCustomSplitInput] = React.useState('');

  React.useEffect(() => {
    if (!isOpen) {
      setMode('root');
      setEqualSplitCount(null);
      setCustomSplitInput('');
    }
  }, [isOpen]);

  const equalSplitAmount = equalSplitCount ? computeEqualSplitAmount(equalSplitTotal, equalSplitCount) : 0;
  const equalSplitAmounts = equalSplitCount ? computeEqualSplitAmounts(equalSplitTotal, equalSplitCount) : [];
  const customSplitAmount = Number(customSplitInput || '0');
  const customSplitRemainder = computeCustomRemainder(equalSplitTotal, customSplitAmount);
  const customValid = isCustomSplitValid(equalSplitTotal, customSplitAmount);

  const handleCustomSplitInputChange = (value: string) => {
    const normalized = value.replace(',', '.');
    if (normalized === '') {
      setCustomSplitInput('');
      return;
    }
    if (/^\d*(\.\d?)?$/.test(normalized)) {
      setCustomSplitInput(normalized);
    }
  };

  const resetToRoot = () => {
    setMode('root');
  };

  return {
    mode,
    equalSplitCount,
    customSplitInput,
    equalSplitAmount,
    equalSplitAmounts,
    customSplitAmount,
    customSplitRemainder,
    customValid,
    setMode,
    setEqualSplitCount,
    handleCustomSplitInputChange,
    resetToRoot,
  };
}
